import React, {useEffect, useState, useRef} from 'react'
import {
    Flex,
    Box,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useParams, useRouteMatch} from 'react-router'
import {useIntl} from 'react-intl'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {Helmet} from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'

// PARTIALS
import Breadcrumb from '../breadcrumb'
import {RecipeHeroBanner} from '../editorial-components'
import IngredientsMenu from './partials/ingredients-menu'
import RecipeStepsMenu from './partials/recipe-steps-menu'
import RecipeProductSuggestions from './partials/recipe-product-suggestions'
import {useGetRecipeDetails} from './utils/use-get-recipe-info'
import LoadingSpinner from '../loading-spinner'
import {BRAND_LABEL} from '../../brand'

const CONTENT_TYPE_RECIPE = 'cooking_recipe'

const RecipeDetails = () => {
    const [productsUsedInRecipe, setProductsUsedInRecipe] = useState([])
    const [toolsUsedInRecipe, setToolsUsedInRecipe] = useState([])
    const [darkBgHeight, setDarkBgHeight] = useState(0)

    const ingredientsRef = useRef(null)
    const stepsRef = useRef(null)
    const {recipe_id} = useParams()
    const path = useRouteMatch()
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const styles = useMultiStyleConfig('RecipeDetails', {})
    const {details} = styles || {}
    const intl = useIntl()
    const location = useLocation()
    const {customParams} = getConfig()

    const {data: recipe} = useGetRecipeDetails(recipe_id, CONTENT_TYPE_RECIPE)
    const utensilsList = recipe?.[0]?.products_utensils || {}

    const recipeProductsSKUs = utensilsList?.product_model?.map((p) => p?.title) || []
    const recipeToolsSKUs = utensilsList?.tools?.map((p) => p?.title) || []

    const fetchProducts = async (codes) => {
        try {
            const token = await getTokenWhenReady()
            const response = await api.shopperProducts.getProducts({
                parameters: {
                    ids: codes.join(','), // join SKUs with comma
                },
                expand: ['availability', 'images'],
                headers: {Authorization: `Bearer ${token}`},
            })
            return response?.data || []
        } catch (e) {
            console.error('Error getting products for suggestions', e)
            return []
        }
    }

    useEffect(() => {
        if (!recipeProductsSKUs.length && !recipeToolsSKUs.length) return

        const fetchAndSetProducts = async (codes, type) => {
            const products = await fetchProducts(codes)
            if (products.length > 0) {
                if (type === 'product_model') {
                    setProductsUsedInRecipe(products)
                } else {
                    setToolsUsedInRecipe(products)
                }
            }
        }

        if (recipeProductsSKUs.length) {
            fetchAndSetProducts(recipeProductsSKUs, 'product_model')
        }
        if (recipeToolsSKUs.length) {
            fetchAndSetProducts(recipeToolsSKUs, 'tools')
        }
    }, [recipe])

    // Build utensils list
    const allUtensils = [
        ...(utensilsList?.utensils?.map(
            (u) => `${u?.quantity || ''} ${u?.utensil?.[0]?.title || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.product_model?.map(
            (u) => `${u?.title || u?.description || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.tools?.map((u) => `${u?.description || ''} ${u?.note || ''}`) || []),
    ]

    const isColumn = useBreakpointValue({base: true, sm: true, md: true, lg: false})

    useEffect(() => {
        const updateHeight = () => {
            if (!recipe) return
            if (!isColumn) {
                if (ingredientsRef.current && stepsRef.current) {
                    const left = ingredientsRef.current.offsetHeight
                    const right = stepsRef.current.offsetHeight
                    setDarkBgHeight(Math.max(left, right))
                }
            } else {
                setDarkBgHeight(ingredientsRef.current.offsetHeight + 75)
            }
        }
        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [recipe, isColumn])

    // Breadcrumbs
    const breadcrumbCategories = [
        {
            id: 'home',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.return_home',
                defaultMessage: 'Return to homepage',
            }),
            c_slugUrl: '/',
        },
        {
            id: 'recipes_page',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.recipes',
                defaultMessage: 'Recipes',
            }),
            c_slugUrl: '/r/recipes',
        },
        {id: 'recipe_detail_page', name: recipe?.[0]?.recipe_title, c_slugUrl: path},
    ]

    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const mainImage = recipe?.[0]?.recipe_media?.recipe_image?.url
    const absolutePath_logoImage = 'https://dam.delonghi.com/600x600/assets/201985'
    const organizationHomepageURL = 'https://' + customParams.currentHost
    const brandName = `De'Longhi Appliances S.r.l`
    const recipeCategory = recipe?.[0]?.recipe_classification?.recipe_category?.[0]?.title || ''
    const datePublished = recipe?.[0]?.created_at?.slice(0, 10)
    const recipeDescription = recipe?.[0]?.description || ''
    const recipeName = recipe?.[0]?.recipe_title || recipe?.[0]?.title || ''
    const recipeImageUrl = recipe?.[0]?.recipe_media?.recipe_image?.url || ''

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: recipeName,
        description: recipeDescription,
        url: currentPath,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbCategories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        url: recipeImageUrl,
        creditText: brandName,
    }

    const ingredientIds = [
        ...new Set(
            recipe?.[0]?.ingredients?.flatMap((ing) =>
                ing?.ingredient?.map((i) => i.mainIngredient?.ingredientId),
            ) || [],
        ),
    ].filter(Boolean)

    const unitIds = [
        ...new Set(
            recipe?.[0]?.ingredients?.flatMap((ing) =>
                ing?.ingredient?.map((i) => i.mainIngredient?.unitId),
            ) || [],
        ),
    ].filter(Boolean)

    const {data: allIngredientsData = []} = useGetRecipeDetails(ingredientIds, 'ingredient')
    const {data: allUnitsData = []} = useGetRecipeDetails(unitIds, 'unit')

    const getFullIngredientList = (ingredients, allIngredientsData, allUnitsData) => {
        if (!ingredients?.length) return []

        const result = []

        for (let ing of ingredients) {
            for (let mainIng of ing?.ingredient || []) {
                const ingredient = allIngredientsData.find(
                    (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                )
                const unit = allUnitsData.find((u) => u.uid === mainIng?.mainIngredient?.unitId)
                result.push(
                    `${mainIng?.mainIngredient?.quantity || ''} ${unit?.title || ''} ${
                        ingredient?.title || ''
                    }${
                        mainIng?.mainIngredient?.notes ? `, ${mainIng.mainIngredient.notes}` : ''
                    }`.trim(),
                )
            }
        }
        return result
    }

    const recipeIngredientsList = getFullIngredientList(
        recipe?.[0]?.ingredients,
        allIngredientsData,
        allUnitsData,
    )

    const recipeInstructions =
        recipe?.[0]?.recipe_steps?.map((step) => ({
            '@type': 'HowToStep',
            text: step.instructions
                ? step.instructions
                      .replace(/<[^>]+>/g, '')
                      .replace(/\s+/g, ' ')
                      .trim()
                : '',
        })) || []

    const recipeSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'Recipe',
        name: recipeName,
        image: mainImage,
        author: {
            '@type': 'Organization',
            name: brandName,
            logo: absolutePath_logoImage,
            url: organizationHomepageURL,
            brand: {
                '@type': 'Brand',
                name: BRAND_LABEL,
            },
        },
        datePublished,
        description: recipeDescription,
        recipeCategory,
        recipeIngredient: recipeIngredientsList,
        recipeInstructions,
    }

    if (!recipe) return <LoadingSpinner />

    return (
        <Flex {...details?.detailsContainer}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(recipeSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {/* Breadcrumbs */}
            <Flex {...details?.breadcrumbsContainer} layerStyle="page">
                <Breadcrumb categories={breadcrumbCategories} w="100%" h="100%" />
            </Flex>

            {/* Hero Section */}
            <Flex justifyContent="center" width="100%" bg="background.light-dark">
                {recipe.map((rec, idx) => (
                    <RecipeHeroBanner
                        key={idx}
                        title={rec?.recipe_title}
                        description={rec?.description}
                        mediaData={
                            rec.recipe_media?.recipe_image ?? rec.recipe_media?.recipe_video ?? null
                        }
                        recipeInfo={{
                            author: rec?.author,
                            time: rec?.recipe_time,
                            classification: rec?.recipe_classification,
                        }}
                    />
                ))}
            </Flex>

            {/* Product Suggestions */}
            {productsUsedInRecipe.length > 0 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_prepare_with.text',
                            defaultMessage: 'This recipe is prepared with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct={true}
                        toolsGroup={toolsUsedInRecipe}
                    />
                </Flex>
            )}

            {/* Ingredients + Steps Section */}
            <Box position="relative" w="100%">
                {/* Background layer */}
                <Box
                    {...styles?.recipeInfoSudoContainer}
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        w: {base: '100%', lg: '50%'},
                        h: {base: `${darkBgHeight}px`, lg: '100%'},
                        bg: 'background.light-dark',
                    }}
                />

                {/* Foreground content */}
                <Flex
                    layerStyle="page"
                    {...details?.recipeInfoContainer}
                    position="relative"
                    zIndex={1}
                >
                    {recipe.map((rec, idx) => (
                        <Box
                            key={`ingredients-${idx}`}
                            ref={ingredientsRef}
                            w={{base: '100%', lg: '50%'}}
                            position={{base: 'relative', lg: 'sticky'}}
                            top={{base: '0', lg: 0}}
                            alignSelf="flex-start"
                        >
                            <IngredientsMenu
                                ingredients={rec?.ingredients}
                                servings={rec?.makes_servings}
                                allUtensils={allUtensils}
                            />
                        </Box>
                    ))}

                    {recipe.map((rec, idx) => (
                        <Box key={`steps-${idx}`} ref={stepsRef} w={{base: '100%', lg: '50%'}}>
                            <RecipeStepsMenu recipeSteps={rec?.recipe_steps} />
                        </Box>
                    ))}
                </Flex>
            </Box>

            {/* Additional product suggestions */}
            {productsUsedInRecipe.length > 1 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_also_prepare_with.text',
                            defaultMessage: 'You can also prepare this recipe* with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct={false}
                    />
                </Flex>
            )}
        </Flex>
    )
}

export default RecipeDetails
